/* @flow */

import type {ReportRowData, TimeseriesReportData, ListItem} from 'nutshell-core/types';
import {combineReducers} from 'redux';
import {createReducer} from 'redux-act';

import * as actions from './editions-actions';

type EditionRecipientListState = {
    isLoading: boolean,
    isFirstLoad: boolean,
    errorMessage?: string,
    listItems: Object[],
    meta: Object,
};

const editionRecipientListDefaultState = {
    isLoading: true,
    isFirstLoad: true,
    errorMessage: undefined,
    listItems: [],
    meta: {},
};

const isFilterDrawerOpen = createReducer(
    {
        [actions.toggleShowFilterDrawer]: (state) => !state,
    },
    false
);

export const editionRecipientListReducer = createReducer(
    {
        // $FlowIgnore - this works correctly from redux-act
        [actions.requestEditionRecipientListData]: (state) => {
            return {...state, isLoading: true, errorMessage: undefined};
        },
        [actions.updateEditionRecipientListData]: (state, {meta, listItems}) => {
            return {
                ...state,
                isLoading: false,
                isFirstLoad: false,
                meta: meta,
                listItems: listItems
                    ? listItems.map((item: ListItem) => {
                          return {
                              ...item,
                              id: item.links ? item.links.entity.id : item.id,
                          };
                      })
                    : undefined,
                errorMessage: undefined,
            };
        },
        [actions.failEditionRecipientListData]: (state, err) => {
            return {...state, isLoading: false, isFirstLoad: false, errorMessage: err};
        },
    },
    editionRecipientListDefaultState
);

type EditionEngagementReportTableData = {
    rows: ?(ReportRowData[]),
    totals: ?ReportRowData,
};
type EditionEngagementReportState = {
    timeseriesChart: {
        isLoading: boolean,
        isTimedOut: boolean,
        isErrored: boolean,
        chartData: ?TimeseriesReportData,
        prefix: string,
    },
};
const editionEngagementReportDefaultState = {
    timeseriesChart: {
        isLoading: true,
        isTimedOut: false,
        isErrored: false,
        chartData: null,
        prefix: '$',
    },
};

const editionEngagementReportReducer = createReducer(
    {
        [actions.updateEditionEngagementReportTableData]: (
            state,
            payload: EditionEngagementReportTableData
        ) => {
            return {...state, tableData: payload};
        },
        // $FlowIgnore - this works correctly from redux-act
        [actions.requestEditionEngagementReportData]: (state) => {
            const newChartState = {
                ...state.timeseriesChart,
                isLoading: true,
                isTimedOut: false,
                isErrored: false,
            };

            return {...state, timeseriesChart: newChartState};
        },
        [actions.updateEditionEngagementReportChartData]: (state, {chartData}) => {
            const newChartState = {
                ...state.timeseriesChart,
                isLoading: false,
                isTimedOut: false,
                isErrored: false,
                chartData,
            };

            return {...state, timeseriesChart: newChartState};
        },
        [actions.failEditionEngagementReportChartData]: (state, err) => {
            if (err) {
                if (err.status === 504 || err.status === 502) {
                    const newChartState = {
                        ...state.timeseriesChart,
                        chartData: null,
                        isLoading: false,
                        isTimedOut: true,
                        isErrored: false,
                    };

                    return {...state, timeseriesChart: newChartState};
                } else {
                    const newChartState = {
                        ...state.timeseriesChart,
                        chartData: null,
                        isLoading: false,
                        isTimedOut: false,
                        isErrored: true,
                    };

                    return {...state, timeseriesChart: newChartState};
                }
            }

            return state;
        },
    },
    editionEngagementReportDefaultState
);

// $FlowFixMe upgrading Flow to v0.92.1 on web
export const editionsReducer = combineReducers({
    editionRecipientList: editionRecipientListReducer,
    editionEngagementReport: editionEngagementReportReducer,
    isFilterDrawerOpen,
});

export type EditionsState = {
    editionRecipientList: EditionRecipientListState,
    editionEngagementReport: EditionEngagementReportState,
    isFilterDrawerOpen: boolean,
};
